import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Plus,
  RefreshCw,
  Shield,
  Trash2,
  Eye,
  X,
  Layers,
  Hash,
  FileText,
} from "lucide-react";

import {
  useCreateExecutiveOfficeMutation,
  useDeleteExecutiveOfficeMutation,
  useGetExecutiveOfficesQuery,
} from "../api/executiveOfficeApi";

import { PERMS } from "../../../utils/perms";
import AccessGate from "../../../components/AccessGate";
import { useAccess } from "../../../utils/useAccess";
import PageGate from "../../../components/PageGate";

// --- Sub-components ---
function StatCard({ title, value }: { title: string; value: string | number }) {
  return (
    <div className="bg-white border rounded-xl p-5 shadow-sm">
      <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
        {title}
      </div>
      <div className="text-2xl font-black text-gray-900 mt-1">{value}</div>
    </div>
  );
}

function msgFromErr(err: any) {
  return err?.data?.message || err?.error || "Request failed";
}

export default function ExecutiveOfficesPage() {
  const { user } = useAccess();
  const {
    data = [],
    isLoading,
    error,
    refetch,
  } = useGetExecutiveOfficesQuery();
  const [createOffice, { isLoading: creating }] =
    useCreateExecutiveOfficeMutation();
  const [deleteOffice, { isLoading: deleting }] =
    useDeleteExecutiveOfficeMutation();

  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  const [form, setForm] = useState({ name: "", description: "", rank: "" });
  const [formErr, setFormErr] = useState<{ name?: string }>({});
  const nameRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (open) setTimeout(() => nameRef.current?.focus(), 0);
  }, [open]);

  const list = useMemo(() => {
    return [...data].sort(
      (a: any, b: any) => (a.rank ?? 9999) - (b.rank ?? 9999)
    );
  }, [data]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setMsg(null);

    if (!form.name.trim()) {
      setFormErr({ name: "Name is required" });
      nameRef.current?.focus();
      return;
    }

    try {
      const res: any = await createOffice({
        name: form.name.trim(),
        description: form.description?.trim() || undefined,
        rank: form.rank ? Number(form.rank) : undefined,
      }).unwrap();

      setMsg({
        type: "success",
        text: res?.message || "Office created successfully",
      });
      setOpen(false);
      setForm({ name: "", description: "", rank: "" });
      setFormErr({});
    } catch (err: any) {
      setMsg({ type: "error", text: msgFromErr(err) });
    }
  };

  const onDelete = async (id: number) => {
    if (
      !window.confirm("Delete this office? This also removes its role mapping.")
    )
      return;
    setMsg(null);
    try {
      const res: any = await deleteOffice(id).unwrap();
      setMsg({ type: "success", text: res?.message || "Office deleted" });
    } catch (err: any) {
      setMsg({ type: "error", text: msgFromErr(err) });
    }
  };

  return (
    <PageGate perm={PERMS.executive_office.access}>
      <div className="space-y-6">
        {/* Header Card */}
        <div className="bg-white border rounded-xl p-5 shadow-sm">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-gray-900 border border-gray-800">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-black text-gray-900 tracking-tight text-xl">
                  Executive Offices
                </h1>
                <p className="text-sm text-gray-500">
                  Logged in as{" "}
                  <span className="font-semibold">{user?.email}</span>
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => refetch()}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border bg-white hover:bg-gray-50 text-sm font-medium transition-colors"
                disabled={isLoading}
              >
                <RefreshCw
                  className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`}
                />
                Refresh
              </button>

              <AccessGate perm={PERMS.executive_office.create}>
                <button
                  onClick={() => {
                    setMsg(null);
                    setOpen(true);
                  }}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-900 text-white hover:bg-gray-800 text-sm font-medium transition-transform active:scale-95 shadow-lg shadow-gray-900/20"
                >
                  <Plus className="w-4 h-4" />
                  New Office
                </button>
              </AccessGate>
            </div>
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard title="Total Offices" value={list.length} />
          <StatCard title="Highest Rank" value={list[0]?.rank ?? "—"} />
          <StatCard
            title="Active Assignments"
            value={list.filter((o) => o.role).length}
          />
        </div>

        {/* Message Notifications */}
        {msg && (
          <div
            className={`p-4 rounded-xl border flex justify-between items-center animate-in fade-in slide-in-from-top-2 ${
              msg.type === "success"
                ? "bg-green-50 border-green-100 text-green-800"
                : "bg-red-50 border-red-200 text-red-800"
            }`}
          >
            <span className="text-sm font-medium">{msg.text}</span>
            <button onClick={() => setMsg(null)}>
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Table Section */}
        <div className="bg-white border rounded-xl overflow-hidden shadow-sm">
          {isLoading ? (
            <div className="py-20 text-center flex flex-col items-center gap-2">
              <RefreshCw className="w-8 h-8 animate-spin text-gray-200" />
              <span className="text-gray-400 text-sm">
                Loading Executive registry...
              </span>
            </div>
          ) : error ? (
            <div className="py-20 text-center text-red-600 font-medium bg-red-50/50">
              Failed to load offices. Please check connection.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead>
                  <tr className="border-b bg-gray-50/50 text-[11px] font-black uppercase tracking-widest text-gray-400">
                    <th className="py-4 px-6 w-20">Rank</th>
                    <th className="py-4 px-6">Office Title</th>
                    <th className="py-4 px-6">Slug/ID</th>
                    <th className="py-4 px-6">Linked Role</th>
                    <AccessGate
                      perm={
                        PERMS.executive_office.detailsAccess ||
                        PERMS.executive_office.delete
                      }
                    >
                      <th className="py-4 px-6 text-right">Actions</th>
                    </AccessGate>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {list.map((o: any) => (
                    <tr
                      key={o.id}
                      className="hover:bg-gray-50/80 transition-colors group"
                    >
                      <td className="py-4 px-6">
                        <span className="px-2 py-1 rounded bg-gray-100 text-gray-600 font-mono text-xs font-bold">
                          #{o.rank ?? "—"}
                        </span>
                      </td>
                      <td className="py-4 px-6">
                        <div className="font-bold text-gray-900">{o.name}</div>
                        <div className="text-[11px] text-gray-400 truncate max-w-[200px]">
                          {o.description || "No description"}
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <code className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded">
                          {o.slug ?? "—"}
                        </code>
                      </td>
                      <td className="py-4 px-6">
                        {o.role?.name ? (
                          <div className="flex items-center gap-1.5 text-gray-700 font-medium">
                            <Shield className="w-3 h-3 text-green-500" />
                            {o.role.name}
                          </div>
                        ) : (
                          <span className="text-gray-300 italic">
                            No role mapping
                          </span>
                        )}
                      </td>
                      <AccessGate
                        perm={
                          PERMS.executive_office.detailsAccess ||
                          PERMS.executive_office.delete
                        }
                      >
                        <td className="py-4 px-6 text-right">
                          <div className="flex justify-end gap-2">
                            <AccessGate
                              perm={PERMS.executive_office.detailsAccess}
                            >
                              <Link
                                to={`/executive-offices/${o.id}`}
                                className="p-2 border rounded-lg bg-white hover:bg-gray-50 text-gray-600 transition-all shadow-sm"
                                title="View Details"
                              >
                                <Eye className="w-4 h-4" />
                              </Link>
                            </AccessGate>

                            <AccessGate perm={PERMS.executive_office.delete}>
                              <button
                                disabled={creating || deleting}
                                onClick={() => onDelete(o.id)}
                                className="p-2 border rounded-lg bg-white hover:bg-red-50 text-red-600 transition-all shadow-sm disabled:opacity-50"
                                title="Delete Office"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </AccessGate>
                          </div>
                        </td>
                      </AccessGate>
                    </tr>
                  ))}
                  {!list.length && (
                    <tr>
                      <td
                        colSpan={5}
                        className="py-16 text-center text-gray-400 italic"
                      >
                        No executive offices found. Create one to get started.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* CREATE MODAL */}
        {open && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 backdrop-blur-sm p-4">
            <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl border p-6 animate-in fade-in zoom-in duration-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-blue-50">
                    <Layers className="w-5 h-5 text-blue-600" />
                  </div>
                  <h2 className="text-xl font-black text-gray-900 tracking-tight">
                    New Executive Office
                  </h2>
                </div>
                <button
                  onClick={() => setOpen(false)}
                  className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>

              <p className="text-sm text-gray-500 mt-2">
                Defining an office automatically generates a matching role and
                slug for permissions.
              </p>

              <form onSubmit={submit} className="mt-6 space-y-4">
                <div>
                  <label className="flex items-center gap-2 text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">
                    <FileText className="w-3 h-3" /> Office Name
                  </label>
                  <input
                    ref={nameRef}
                    value={form.name}
                    onChange={(e) => {
                      setForm((p) => ({ ...p, name: e.target.value }));
                      if (formErr.name) setFormErr({});
                    }}
                    placeholder="e.g. Chief Executive Officer"
                    className={`w-full border rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-gray-900/5 focus:border-gray-900 transition-all ${
                      formErr.name
                        ? "border-red-300 ring-red-50"
                        : "border-gray-200"
                    }`}
                    disabled={creating}
                  />
                  {formErr.name && (
                    <div className="text-xs text-red-600 mt-1 font-medium">
                      {formErr.name}
                    </div>
                  )}
                </div>

                <div>
                  <label className="flex items-center gap-2 text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">
                    Description
                  </label>
                  <textarea
                    value={form.description}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, description: e.target.value }))
                    }
                    placeholder="Brief description of responsibilities..."
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-gray-900/5 focus:border-gray-900 transition-all resize-none"
                    rows={3}
                    disabled={creating}
                  />
                </div>

                <div>
                  <label className="flex items-center gap-2 text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">
                    <Hash className="w-3 h-3" /> Priority Rank
                  </label>
                  <input
                    type="number"
                    value={form.rank}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, rank: e.target.value }))
                    }
                    placeholder="e.g. 1"
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-gray-900/5 focus:border-gray-900 transition-all"
                    disabled={creating}
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="flex-1 px-4 py-3 rounded-xl border font-bold text-gray-600 hover:bg-gray-50 transition-colors"
                    disabled={creating}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={creating}
                    className="flex-1 px-4 py-3 rounded-xl bg-gray-900 text-white font-bold hover:bg-gray-800 disabled:opacity-50 transition-all shadow-lg shadow-gray-900/20 flex items-center justify-center gap-2"
                  >
                    {creating ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <Plus className="w-4 h-4" />
                    )}
                    {creating ? "Creating..." : "Create Office"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </PageGate>
  );
}
